import {
  addDoc, collection, deleteDoc, doc, onSnapshot, serverTimestamp, setDoc,
  type Timestamp,
} from "firebase/firestore";
import { db } from "./ambiente";

/* ============================================================
   Denunciar e bloquear

   Exigência das duas lojas para app com conteúdo de gente: precisa haver
   um caminho de denúncia, um de bloqueio, e remoção em 24 h. As regras
   do Firestore já existiam; faltava a interface.

   As duas coisas são diferentes de propósito. DENUNCIAR é um pedido de
   ação para quem administra, e demora. BLOQUEAR resolve na hora e só
   para quem bloqueou — não avisa ninguém, não apaga nada, e é isso que
   torna seguro usar sem pensar duas vezes.
   ============================================================ */

export type TipoDeDenuncia = "prato" | "comentario" | "mensagem" | "perfil";

/** Os motivos são fechados: campo livre vira caixa de desabafo, e aí
    ninguém consegue priorizar a fila. O texto fica opcional ao lado. */
export const MOTIVOS = [
  /* Primeiro da lista de propósito: é o motivo que a política de
     segurança infantil promete (`nhomi.app/seguranca-infantil`), e o
     aviso de denúncia o trata como urgente. */
  { id: "menor", label: "Envolve criança ou adolescente" },
  { id: "spam", label: "Spam ou propaganda" },
  { id: "impróprio", label: "Conteúdo impróprio" },
  { id: "assédio", label: "Assédio ou ataque pessoal" },
  { id: "falso", label: "Não é comida / prato falso" },
  { id: "outro", label: "Outro" },
] as const;

export type MotivoId = typeof MOTIVOS[number]["id"];

export interface ReportDoc {
  /** Quem denunciou. A regra exige que bata com quem está logado. */
  uid: string;
  tipo: TipoDeDenuncia;
  motivo: MotivoId;
  texto: string;
  /** Onde está o conteúdo, para quem for olhar chegar lá. */
  cid: string;
  pid: string;
  alvoUid: string;
  at: Timestamp;
  /** Escrito por quem resolve, pelo painel. Nasce ausente. */
  resolvido?: boolean;
}

export async function denunciar(
  quem: string,
  alvo: { tipo: TipoDeDenuncia; cid: string; pid: string; alvoUid: string },
  motivo: MotivoId,
  texto: string,
): Promise<void> {
  await addDoc(collection(db, "reports"), {
    uid: quem,
    tipo: alvo.tipo,
    motivo,
    texto: texto.trim().slice(0, 500),
    cid: alvo.cid,
    pid: alvo.pid,
    alvoUid: alvo.alvoUid,
    at: serverTimestamp(),
  });
}

/* ---------- bloqueio ---------- */

const blocosRef = (uid: string) => collection(db, "users", uid, "blocks");

/**
 * Quem esta pessoa bloqueou.
 *
 * Fica em `users/{uid}/blocks` e só quem bloqueou lê — bloqueio que a
 * outra parte consegue enxergar deixa de ser proteção e vira briga.
 */
export function watchBloqueados(uid: string, cb: (uids: Set<string>) => void): () => void {
  return onSnapshot(
    blocosRef(uid),
    (snap) => cb(new Set(snap.docs.map((d) => d.id))),
    () => cb(new Set()),
  );
}

export async function bloquear(uid: string, alvoUid: string, nome: string): Promise<void> {
  // O nome vai junto para a lista em Conta poder mostrar quem é sem ler o
  // perfil de alguém que a pessoa não quer mais ver.
  await setDoc(doc(blocosRef(uid), alvoUid), { nome, at: serverTimestamp() });
}

export async function desbloquear(uid: string, alvoUid: string): Promise<void> {
  await deleteDoc(doc(blocosRef(uid), alvoUid));
}

export interface Bloqueado { uid: string; nome: string }

export function watchListaDeBloqueados(
  uid: string, cb: (lista: Bloqueado[]) => void,
): () => void {
  return onSnapshot(
    blocosRef(uid),
    (snap) => cb(snap.docs.map((d) => ({
      uid: d.id,
      nome: String(d.data()["nome"] ?? "alguém"),
    }))),
    () => cb([]),
  );
}
